/**
 * OutboundChannelSubHeader — sub-header notice for an outbound channel record
 * that has been auto-paused after repeated delivery failures.
 *
 * There's no disabled-reason property to read anymore (see P12) — this
 * derives "was this pause automatic" from the timeline: `useOutboundPauseSource`
 * finds the newest `ahoy_outbound_enabled=off` event and reports who wrote it.
 * Self-gates to null unless the object is an outbound channel, currently
 * disabled, and that source resolves to `'system'` (as opposed to a manual
 * pause, still loading, or unresolvable) — so it is safe to render from the
 * generic fall-through for every entity type without a registered sub-header.
 * Either way, re-enabling stays the same one-property write via the inline
 * Outbound row on the record — this notice hiding is never the only way back
 * on.
 */

import { useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import type { CrmObject } from '@/types/crm';

import { useOutboundChannelState } from '../hooks/useOutboundChannelState';
import { useOutboundPauseSource } from '../hooks/useOutboundPauseSource';

export function OutboundChannelSubHeader({ object }: { object: CrmObject }) {
  const { isOutboundChannel, enabled, canToggle, setEnabled } =
    useOutboundChannelState(object);
  const pauseSource = useOutboundPauseSource(object, {
    paused: isOutboundChannel && !enabled,
  });

  const handleReEnable = useCallback(() => {
    setEnabled(true);
  }, [setEnabled]);

  if (!isOutboundChannel || enabled || pauseSource !== 'system') {
    return null;
  }

  return (
    <div className='flex items-center flex-wrap gap-2.5 text-xs md:text-sm'>
      <span className='text-[var(--color-text-warning-primary)]'>
        Paused automatically after repeated failures
      </span>
      {canToggle && (
        <Button color='secondary' size='xs' onClick={handleReEnable}>
          Re-enable
        </Button>
      )}
    </div>
  );
}
